import {
  type AIModel,
  type AIModelService,
  AUTO_MODEL_ID,
  type ReasoningModeId,
} from '@affine/core/modules/ai-button/services/models';
import type { CopilotChatHistoryFragment } from '@affine/graphql';
import { menu, popMenu } from '@blocksuite/affine/components/context-menu';
import { SignalWatcher, WithDisposable } from '@blocksuite/affine/global/lit';
import { unsafeCSSVarV2 } from '@blocksuite/affine/shared/theme';
import { InformationIcon } from '@blocksuite/icons/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { computed } from '@preact/signals-core';
import { css, html, type PropertyValues } from 'lit';
import { property, state } from 'lit/decorators.js';

import {
  sortModelsByTierAndCost,
  TIER_CONFIG,
  TIER_ORDER,
  tierPillStyleString,
} from '../../utils/model-tier';
import {
  effectiveReasoningModesForModel,
  reasoningModeDisplayLabel,
} from '../../utils/reasoning-mode';
import { getChatInputPortalReferenceElement } from '../ai-chat-chips/chat-input-portal-style';
import {
  configureStreamLainDropdownMenu,
  popupTargetFromBodyElement,
  streamLainDropdownMenuBoundaryWidthCss,
  type StreamLainDropdownMenuHandler,
  streamLainDropdownMenuMiddleware,
} from './dropdown-menu-chrome';

const PREFERENCE_MENU_STYLE_ID =
  'streamlain-chat-input-preference-menu-content-style';
const PREFERENCE_MENU_WIDTH = '360px';

function ensurePreferenceMenuContentStyle() {
  if (document.getElementById(PREFERENCE_MENU_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = PREFERENCE_MENU_STYLE_ID;
  style.textContent = `
    .streamlain-chat-input-dropdown-menu .effort-thumb,
    .streamlain-chat-input-dropdown-menu-closing .effort-thumb {
      position: absolute;
      top: 0;
      height: 22px;
      box-sizing: border-box;
      border-radius: 8px;
      background: var(--streamlain-v2-layer-background-overlayPanel);
      border: 1px solid var(--streamlain-v2-layer-insideBorder-border);
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
      color: var(--streamlain-v2-icon-secondary);
      pointer-events: none;
      transition:
        left 0.18s cubic-bezier(0.3, 0.9, 0.4, 1),
        background-color 0.16s ease,
        border-color 0.16s ease,
        box-shadow 0.16s ease,
        color 0.16s ease,
        transform 0.16s ease;
    }

    .streamlain-chat-input-dropdown-menu .effort-slider,
    .streamlain-chat-input-dropdown-menu-closing .effort-slider {
      box-sizing: border-box;
      width: 100%;
      padding: 8px 12px;
      border-radius: 8px;
      transition:
        background-color 0.16s ease,
        color 0.16s ease;
    }

    .streamlain-chat-input-dropdown-menu .effort-track-bg,
    .streamlain-chat-input-dropdown-menu-closing .effort-track-bg,
    .streamlain-chat-input-dropdown-menu .effort-fill,
    .streamlain-chat-input-dropdown-menu-closing .effort-fill,
    .streamlain-chat-input-dropdown-menu .effort-mark,
    .streamlain-chat-input-dropdown-menu-closing .effort-mark {
      transition:
        background-color 0.16s ease,
        filter 0.16s ease,
        opacity 0.16s ease,
        width 0.18s cubic-bezier(0.3, 0.9, 0.4, 1);
    }

    .streamlain-chat-input-dropdown-menu .effort-mark,
    .streamlain-chat-input-dropdown-menu-closing .effort-mark {
      background: var(--streamlain-text-secondary-color);
    }

    .streamlain-chat-input-dropdown-menu .effort-thumb::before,
    .streamlain-chat-input-dropdown-menu-closing .effort-thumb::before {
      content: '';
      position: absolute;
      left: 50%;
      top: 50%;
      width: 14px;
      height: 10px;
      transform: translate(-50%, -50%);
      background: linear-gradient(
        90deg,
        currentColor 0 2px,
        transparent 2px 6px,
        currentColor 6px 8px,
        transparent 8px 12px,
        currentColor 12px 14px
      );
      border-radius: 2px;
      opacity: 0.72;
    }

    .streamlain-chat-input-dropdown-menu .effort-slider:hover {
      background: var(--streamlain-v2-layer-background-hoverOverlay);
      color: var(--streamlain-v2-text-primary);
    }

    .streamlain-chat-input-dropdown-menu
      .effort-slider:hover
      .effort-track-bg {
      background: var(--streamlain-v2-layer-background-hoverOverlay);
      opacity: 0.95;
    }

    .streamlain-chat-input-dropdown-menu
      .effort-slider:hover
      .effort-fill {
      filter: brightness(1.08);
    }

    .streamlain-chat-input-dropdown-menu .effort-slider:hover .effort-mark {
      background: var(--streamlain-v2-layer-pureWhite);
      opacity: 0.85;
    }

    .streamlain-chat-input-dropdown-menu
      .effort-slider:hover
      .effort-thumb {
      background: var(--streamlain-v2-layer-background-overlayPanel);
      border-color: var(--streamlain-v2-button-primary);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.32);
      color: var(--streamlain-v2-icon-primary);
      transform: scale(1.04);
    }

    .streamlain-chat-input-dropdown-menu
      .effort-slider:hover
      .effort-thumb::before {
      opacity: 1;
    }
  `;
  document.head.append(style);
}

function modelTriggerLabelParts(displayName: string) {
  const streamLainModel = /^(StreamLain(?:\s+AI)?)\s+(.+)$/iu.exec(
    displayName.trim()
  );
  if (!streamLainModel) {
    return { brandPrefix: '', modelName: displayName };
  }
  return { brandPrefix: streamLainModel[1], modelName: streamLainModel[2] };
}

export class ChatInputPreference extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    .chat-input-preference-trigger {
      display: flex;
      align-items: center;
      gap: 4px;
      width: max-content;
      max-width: 100%;
      height: 28px;
      min-height: 28px;
      box-sizing: border-box;
      padding: 0;
      color: var(--streamlain-v2-icon-primary);
      transition: all 0.23s ease;
      border-radius: 8px;
      background: transparent;
      border: none;
      cursor: pointer;
    }
    .chat-input-preference-trigger:hover,
    .chat-input-preference-trigger[data-menu-open='true'] {
      background-color: var(--streamlain-v2-layer-background-hoverOverlay);
    }
    .chat-input-preference-trigger-label {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      line-height: 22px;
      font-weight: 500;
      max-width: 100%;
      min-width: 0;
      padding: 0 4px;
      white-space: nowrap;
      overflow: hidden;
      opacity: 0.8;
      transition: opacity 0.16s ease;
    }
    .chat-input-preference-trigger:hover .chat-input-preference-trigger-label,
    .chat-input-preference-trigger[data-menu-open='true']
      .chat-input-preference-trigger-label {
      opacity: 1;
    }
    .chat-input-preference-trigger-brand {
      flex: 0 0 auto;
      white-space: nowrap;
    }
    .chat-input-preference-trigger-model {
      flex: 1 1 auto;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .preference-action {
      white-space: nowrap;
    }
    .model-load-error {
      max-width: 320px;
      white-space: normal;
      color: ${unsafeCSSVarV2('status/error')};
      font-size: 12px;
      line-height: 18px;
      padding: 2px 0;
    }
    .ai-active-model-name {
      font-size: 14px;
      color: ${unsafeCSSVarV2('text/secondary')};
      line-height: 22px;
      margin-left: 40px;
    }
    .ai-model-prefix {
      width: 20px;
      height: 20px;
    }
    .ai-model-prefix svg {
      color: ${unsafeCSSVarV2('icon/activated')};
    }
    .ai-model-postfix svg:hover {
      color: ${unsafeCSSVarV2('icon/activated')};
    }
    .ai-model-version {
      font-size: 12px;
      color: ${unsafeCSSVarV2('text/tertiary')};
      line-height: 20px;
      margin-right: 40px;
    }
  `;

  @property({ attribute: false })
  accessor session!: CopilotChatHistoryFragment | null | undefined;
  // --------- model props end ---------

  // --------- extended thinking props start ---------
  @property({ attribute: false })
  accessor extendedThinking: boolean = false;

  @property({ attribute: false })
  accessor onExtendedThinkingChange:
    | ((extendedThinking: boolean) => void)
    | undefined;

  @property({ attribute: false })
  accessor reasoningEffort: ReasoningModeId = 'medium';

  @property({ attribute: false })
  accessor onReasoningEffortChange:
    | ((effort: ReasoningModeId) => void)
    | undefined;
  // --------- extended thinking props end ---------

  @property({ attribute: false })
  accessor aiModelService!: AIModelService;

  @property({ attribute: false })
  accessor keySource: 'user' | 'workspace' | 'server' | 'none' = 'none';

  @property({ attribute: false })
  accessor availableInlineSize: number | null = null;

  @state()
  accessor hideBrandPrefix = false;

  @state()
  accessor menuOpen = false;

  private _currentMenuHandler: StreamLainDropdownMenuHandler | null = null;

  private measurementCanvas: HTMLCanvasElement | null = null;

  model = computed(() => {
    const modelId = this.aiModelService.modelId.value;
    const defaultModel = this.aiModelService.models.value.find(
      model => model.isDefault
    );
    // sl-sf9o: 'auto' uses default model for UI purposes (e.g. Extended Thinking check)
    if (modelId === AUTO_MODEL_ID) return defaultModel ?? null;
    const activeModel = this.aiModelService.models.value.find(
      model => model.id === modelId
    );
    return activeModel || defaultModel;
  });

  private _getReasoningLabel(
    model:
      | Pick<AIModel, 'reasoningProfile' | 'supportsReasoning'>
      | null
      | undefined
  ) {
    if (model?.reasoningProfile) return model.reasoningProfile.label;
    if (model?.supportsReasoning) return 'Reasoning';
    return 'Extended Thinking';
  }

  protected override updated(changedProperties: PropertyValues): void {
    super.updated(changedProperties);
    this._updateBrandPrefixVisibility();
  }

  private _getFullDisplayName() {
    const isAuto =
      this.aiModelService.modelId.value === AUTO_MODEL_ID &&
      this.aiModelService.routingAvailable.value;
    if (isAuto) return 'Auto';
    const name = this.model.value?.name ?? this.model.value?.category;
    if (name) {
      const { modelName } = modelTriggerLabelParts(name);
      const reasoningLabel = this.extendedThinking
        ? reasoningModeDisplayLabel(this.reasoningEffort)
        : '';
      return [modelName || name, reasoningLabel].filter(Boolean).join(' ');
    }
    // No model resolvable (registry unreachable, no local snapshot) —
    // show an honest offline state instead of an empty trigger.
    return this.aiModelService.loadError.value ? 'AI offline' : '';
  }

  private _measureTextWidth(text: string, sourceElement: HTMLElement) {
    this.measurementCanvas ??= document.createElement('canvas');
    const context = this.measurementCanvas.getContext('2d');
    if (!context) return 0;

    const style = getComputedStyle(sourceElement);
    context.font = [
      style.fontStyle,
      style.fontVariant,
      style.fontWeight,
      style.fontSize,
      style.fontFamily,
    ].join(' ');
    return context.measureText(text).width;
  }

  private _updateBrandPrefixVisibility() {
    const availableInlineSize = this.availableInlineSize;
    const { brandPrefix, modelName } = modelTriggerLabelParts(
      this._getFullDisplayName()
    );
    if (!brandPrefix || availableInlineSize === null) {
      if (this.hideBrandPrefix) {
        this.hideBrandPrefix = false;
      }
      return;
    }

    const trigger = this.querySelector<HTMLElement>(
      '.chat-input-preference-trigger'
    );
    const label = this.querySelector<HTMLElement>(
      '.chat-input-preference-trigger-label'
    );
    if (!trigger || !label) return;

    const triggerStyle = getComputedStyle(trigger);
    const labelStyle = getComputedStyle(label);
    const horizontalPadding =
      (Number.parseFloat(triggerStyle.paddingLeft) || 0) +
      (Number.parseFloat(triggerStyle.paddingRight) || 0) +
      (Number.parseFloat(labelStyle.paddingLeft) || 0) +
      (Number.parseFloat(labelStyle.paddingRight) || 0);
    const labelGap = Number.parseFloat(labelStyle.columnGap || '0') || 0;
    const fullLabelWidth =
      this._measureTextWidth(brandPrefix, label) +
      labelGap +
      this._measureTextWidth(modelName, label) +
      horizontalPadding;
    const shouldHideBrandPrefix =
      availableInlineSize < Math.ceil(fullLabelWidth);

    if (this.hideBrandPrefix !== shouldHideBrandPrefix) {
      this.hideBrandPrefix = shouldHideBrandPrefix;
    }
  }

  private _closeCurrentPreferenceMenu(afterClose?: () => void) {
    const handler = this._currentMenuHandler;
    if (!handler) {
      afterClose?.();
      return;
    }
    if (handler.closeWithAnimation) {
      handler.closeWithAnimation(afterClose);
      return;
    }
    handler.close();
    afterClose?.();
  }

  openPreference(e: Event) {
    const element = e.currentTarget;
    if (!(element instanceof HTMLElement)) return;
    this._showPreferenceMenu(element);
  }

  /**
   * Build and show the preference popup anchored to the given element.
   * Separated from openPreference() so the close→reopen cycle can pass
   * a stable element reference (e.currentTarget becomes null after the
   * event handler returns, breaking requestAnimationFrame callbacks).
   */
  /**
   * Inline SVG provider icon for model rows.
   * Uses official brand paths (Simple Icons / Wikimedia Commons).
   */
  private _providerIcon(providerId: string) {
    if (providerId.includes('anthropic')) {
      return html`<svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M17.3041 3.541h-3.6718l6.696 16.918H24Zm-10.6082 0L0 20.459h3.7442l1.3693-3.5527h7.0052l1.3693 3.5528h3.7442L10.5363 3.5409Zm-.3712 10.2232 2.2914-5.9456 2.2914 5.9456Z"
          fill="currentColor"
        />
      </svg>`;
    }
    if (providerId.includes('google')) {
      return html`<svg
        width="14"
        height="14"
        viewBox="0 0 65 65"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M32.447 0c.68 0 1.273.465 1.439 1.125a38.904 38.904 0 001.999 5.905c2.152 5 5.105 9.376 8.854 13.125 3.751 3.75 8.126 6.703 13.125 8.855a38.98 38.98 0 005.906 1.999c.66.166 1.124.758 1.124 1.438 0 .68-.464 1.273-1.125 1.439a38.902 38.902 0 00-5.905 1.999c-5 2.152-9.375 5.105-13.125 8.854-3.749 3.751-6.702 8.126-8.854 13.125a38.973 38.973 0 00-2 5.906 1.485 1.485 0 01-1.438 1.124c-.68 0-1.272-.464-1.438-1.125a38.913 38.913 0 00-2-5.905c-2.151-5-5.103-9.375-8.854-13.125-3.75-3.749-8.125-6.702-13.125-8.854a38.973 38.973 0 00-5.905-2A1.485 1.485 0 010 32.448c0-.68.465-1.272 1.125-1.438a38.903 38.903 0 005.905-2c5-2.151 9.376-5.104 13.125-8.854 3.75-3.749 6.703-8.125 8.855-13.125a38.972 38.972 0 001.999-5.905A1.485 1.485 0 0132.447 0z"
          fill="url(#g-grad)"
        />
        <defs>
          <linearGradient
            id="g-grad"
            x1="18"
            y1="43"
            x2="52"
            y2="15"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#4285F4" />
            <stop offset=".5" stop-color="#9B72CB" />
            <stop offset="1" stop-color="#D96570" />
          </linearGradient>
        </defs>
      </svg>`;
    }
    if (providerId.includes('openai')) {
      return html`<svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 11.709.36 6.058 6.058 0 0 0 5.626 2.6 5.985 5.985 0 0 0 1.628 5.5a6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.92 23.64a6.056 6.056 0 0 0 6.083-2.24 5.985 5.985 0 0 0 3.998-2.9 6.046 6.046 0 0 0-.743-7.097"
          fill="currentColor"
        />
      </svg>`;
    }
    return html``;
  }

  /**
   * sl-opil.15.2: Build model selection menu in tier order.
   * Keep all model rows in one menu group so model rows do not get separated
   * by group dividers.
   */
  private _buildModelMenuGroups(): ReturnType<typeof menu.group>[] {
    const models = this.aiModelService.models.value;
    if (models.length === 0) return [];

    const currentModelId = this.aiModelService.modelId.value;
    const currentModel = this.model.value;

    // Group models by tier
    const tierGroups = new Map<string, typeof models>();
    for (const m of models) {
      const tier = m.tier || 'standard';
      if (!tierGroups.has(tier)) {
        tierGroups.set(tier, []);
      }
      tierGroups.get(tier)!.push(m);
    }

    const items: ReturnType<typeof menu.action>[] = [];

    for (const tier of TIER_ORDER) {
      const tierModels = tierGroups.get(tier);
      if (!tierModels || tierModels.length === 0) continue;

      const cfg = TIER_CONFIG[tier];

      // Sort within tier by costMultiplier descending so the strongest model in
      // each quality tier stays on top.
      const sorted = sortModelsByTierAndCost(
        tierModels,
        model => model.costMultiplier
      );

      items.push(
        ...sorted.map(m => {
          const isSelected =
            m.id === currentModelId ||
            (!currentModelId && m.id === currentModel?.id);
          const isStreamLainAIModel = m.providerId === 'streamlain-ai';
          // Plan-based model lock removed — all provisioned models selectable

          return menu.action({
            name: m.name,
            isSelected,
            postfix: html``,
            label: () => html`
              <span
                style="display:flex;align-items:center;gap:6px;width:100%;${isSelected
                  ? 'font-weight:500;'
                  : ''}"
              >
                ${isStreamLainAIModel
                  ? html``
                  : html`<span
                      style="display:flex;flex-shrink:0;color:var(--streamlain-text-secondary-color)"
                      >${this._providerIcon(m.providerId)}</span
                    >`}
                <span style="flex:1;white-space:nowrap;font-size:13px"
                  >${m.name}</span
                >
                ${m.isDefault
                  ? html`<span
                      style="font-size:10px;font-weight:500;padding:1px 5px;border-radius:10px;background:var(--streamlain-v2-button-primary);color:var(--streamlain-v2-layer-pureWhite);white-space:nowrap"
                      >Default</span
                    >`
                  : html``}
                ${isStreamLainAIModel
                  ? html``
                  : html`<span
                        style="font-size:11px;color:var(--streamlain-text-secondary-color);min-width:32px;text-align:right"
                        >${m.costMultiplier}x</span
                      >
                      <span style="${tierPillStyleString(tier)}"
                        >${cfg.label}</span
                      >`}
              </span>
            `,
            select: () => {
              this.aiModelService.setModel(m.id);
              // Update row highlighting in-place — no menu close/reopen.
              // Find all model buttons and toggle the selected-item class.
              const menuEl = this._currentMenuHandler?.menu?.menuElement as
                | HTMLElement
                | undefined;
              if (menuEl) {
                menuEl
                  .querySelectorAll('.affine-menu-button')
                  .forEach(btn => btn.classList.remove('selected-item'));
                // The click target is the current button — re-add selected
                const buttons = menuEl.querySelectorAll('.affine-menu-button');
                buttons.forEach(btn => {
                  if (btn.textContent?.includes(m.name)) {
                    btn.classList.add('selected-item');
                  }
                });
              }
            },
            class: { 'preference-action': true },
          });
        })
      );
    }

    return items.length > 0 ? [menu.group({ items })] : [];
  }

  private _showPreferenceMenu(element: HTMLElement) {
    ensurePreferenceMenuContentStyle();
    const thinkingItems: ReturnType<typeof menu.action>[] = [];

    // Reasoning control stays visible; unsupported models render it disabled.
    const currentModel = this.model.value;
    const reasoningModes = effectiveReasoningModesForModel(
      currentModel ?? undefined
    );
    const modelSupportsReasoning = reasoningModes.length > 0;

    if (currentModel) {
      const sliderModes = [
        { id: 'off' as ReasoningModeId, label: 'Off' },
        ...(modelSupportsReasoning
          ? reasoningModes
          : ([
              { id: 'low' as ReasoningModeId, label: 'Low' },
              { id: 'medium' as ReasoningModeId, label: 'Medium' },
              { id: 'high' as ReasoningModeId, label: 'High' },
              { id: 'xhigh' as ReasoningModeId, label: 'XHigh' },
            ] as const)),
      ];
      const THUMB_W = 26;
      const effortIndexOf = (id: ReasoningModeId | undefined) =>
        sliderModes.findIndex(mode => mode.id === id);
      const sliderFraction = (idx: number) =>
        sliderModes.length > 1 ? idx / (sliderModes.length - 1) : 0;
      const applySliderIndex = (slider: HTMLElement, idx: number) => {
        const f = sliderFraction(idx);
        const thumb = slider.querySelector(
          '.effort-thumb'
        ) as HTMLElement | null;
        const fill = slider.querySelector('.effort-fill') as HTMLElement | null;
        const leftLabel = slider.querySelector(
          '.effort-left-label'
        ) as HTMLElement | null;
        const track = slider.querySelector(
          '.effort-track'
        ) as HTMLElement | null;
        if (thumb) thumb.style.left = `calc((100% - ${THUMB_W}px) * ${f})`;
        if (fill) {
          fill.style.opacity = idx === 0 ? '0' : '0.85';
          fill.style.width = `calc((100% - ${THUMB_W}px) * ${f} + ${THUMB_W}px)`;
        }
        if (leftLabel) leftLabel.textContent = idx === 0 ? 'Off' : 'Faster';
        if (track) {
          track.setAttribute('aria-valuenow', String(idx));
          track.setAttribute(
            'aria-valuetext',
            sliderModes[idx]?.label ?? 'Off'
          );
        }
      };

      // Single slider: first stop disables reasoning, every other stop enables
      // it with the selected provider-specific effort.
      const onEffortChange = this.onReasoningEffortChange;
      const onExtendedThinkingChange = this.onExtendedThinkingChange;
      const initialIndex = this.extendedThinking
        ? Math.max(1, effortIndexOf(this.reasoningEffort))
        : 0;

      const selectFromPointer = (e: PointerEvent) => {
        if (!modelSupportsReasoning) return;
        const track = e.currentTarget as HTMLElement;
        const rect = track.getBoundingClientRect();
        const usable = Math.max(1, rect.width - THUMB_W);
        const x = Math.min(
          Math.max(e.clientX - rect.left - THUMB_W / 2, 0),
          usable
        );
        const idx = Math.round((x / usable) * (sliderModes.length - 1));
        if (track.dataset.effortIndex === String(idx)) return;
        track.dataset.effortIndex = String(idx);
        const slider = track.closest('.effort-slider') as HTMLElement | null;
        if (slider) applySliderIndex(slider, idx);
        const mode = sliderModes[idx];
        if (!mode) return;
        const nextEnabled = mode.id !== 'off';
        this.extendedThinking = nextEnabled;
        onExtendedThinkingChange?.(nextEnabled);
        onEffortChange?.(mode.id);
      };

      thinkingItems.push(() => {
        return html`
          <div
            class="reasoning-control preference-action"
            style=${modelSupportsReasoning ? '' : 'opacity:0.45;'}
          >
            <div
              class="effort-slider"
              style=${modelSupportsReasoning ? '' : 'pointer-events:none;'}
            >
              <div
                style="display:flex;align-items:baseline;justify-content:space-between;margin-bottom:6px;font-size:12px;color:var(--streamlain-text-secondary-color)"
              >
                <span class="effort-left-label"
                  >${initialIndex === 0 ? 'Off' : 'Faster'}</span
                >
                <span>Smarter</span>
              </div>
              <div
                class="effort-track"
                data-effort-index=${initialIndex}
                role="slider"
                aria-label=${this._getReasoningLabel(currentModel)}
                aria-disabled=${modelSupportsReasoning ? 'false' : 'true'}
                aria-valuemin="0"
                aria-valuemax=${sliderModes.length - 1}
                aria-valuenow=${initialIndex}
                aria-valuetext=${sliderModes[initialIndex]?.label ?? 'Off'}
                style="position:relative;height:22px;cursor:pointer;touch-action:none;user-select:none"
                @pointerdown=${(e: PointerEvent) => {
                  e.stopPropagation();
                  e.preventDefault();
                  (e.currentTarget as HTMLElement).setPointerCapture(
                    e.pointerId
                  );
                  selectFromPointer(e);
                }}
                @pointermove=${(e: PointerEvent) => {
                  if ((e.buttons & 1) === 0) return;
                  selectFromPointer(e);
                }}
                @click=${(e: Event) => e.stopPropagation()}
              >
                <div
                  class="effort-track-bg"
                  style="position:absolute;left:0;right:0;top:3px;bottom:3px;border-radius:8px;background:var(--streamlain-v2-layer-background-secondary)"
                ></div>
                <div
                  class="effort-fill"
                  style="position:absolute;left:0;top:3px;bottom:3px;border-radius:8px;background:var(--streamlain-v2-button-primary);opacity:${initialIndex ===
                  0
                    ? '0'
                    : '0.85'};width:calc((100% - ${THUMB_W}px) * ${sliderFraction(
                    initialIndex
                  )} + ${THUMB_W}px);transition:width 0.18s cubic-bezier(0.3, 0.9, 0.4, 1)"
                ></div>
                ${sliderModes.map(
                  (mode, i) => html`
                    <span
                      class="effort-mark"
                      title=${mode.label}
                      style="position:absolute;top:50%;left:calc((100% - ${THUMB_W}px) * ${sliderFraction(
                        i
                      )} + ${THUMB_W /
                      2}px);width:4px;height:4px;margin:-2px 0 0 -2px;border-radius:50%;opacity:0.55;pointer-events:none"
                    ></span>
                  `
                )}
                <div
                  class="effort-thumb"
                  style="width:${THUMB_W}px;left:calc((100% - ${THUMB_W}px) * ${sliderFraction(
                    initialIndex
                  )})"
                ></div>
              </div>
            </div>
          </div>
        `;
      });
    }

    const keySourceItems = [];
    if (this.keySource === 'workspace') {
      keySourceItems.push(
        menu.action({
          name: 'Model set by workspace admin',
          prefix: InformationIcon(),
          class: { 'preference-action': true },
          select: () => {},
        })
      );
    } else if (this.keySource === 'user') {
      keySourceItems.push(
        menu.action({
          name: 'Change model in AI Settings',
          prefix: InformationIcon(),
          class: { 'preference-action': true },
          select: () => {},
        })
      );
    } else if (this.keySource === 'server') {
      keySourceItems.push(
        menu.action({
          name: 'Model set by server admin',
          prefix: InformationIcon(),
          class: { 'preference-action': true },
          select: () => {},
        })
      );
    }

    // sl-sf9o: Auto-routing toggle — only shown when workspace admin enabled it
    const routingAvailable = this.aiModelService.routingAvailable.value;

    // sl-opil.15.2: Model selection groups (one per provider)
    const modelGroups = this._buildModelMenuGroups();
    const modelLoadError = this.aiModelService.loadError.value;

    const groups = [];
    if (routingAvailable) {
      const isAutoRouting = this.aiModelService.modelId.value === AUTO_MODEL_ID;
      groups.push(
        menu.group({
          items: [
            menu.toggleSwitch({
              name: 'Auto',
              on: isAutoRouting,
              onChange: (value: boolean) => {
                if (value) {
                  this.aiModelService.setModel(AUTO_MODEL_ID);
                } else {
                  const defaultModel = this.aiModelService.models.value.find(
                    m => m.isDefault
                  );
                  if (defaultModel) {
                    this.aiModelService.setModel(defaultModel.id);
                  } else {
                    this.aiModelService.resetModel();
                  }
                }
                this._closeCurrentPreferenceMenu(() =>
                  requestAnimationFrame(() => this._showPreferenceMenu(element))
                );
              },
              class: { 'preference-action': true },
            }),
          ],
        })
      );
    }
    if (modelLoadError) {
      groups.push(
        menu.group({
          items: [
            menu.action({
              name: 'AI models unavailable',
              label: () => html`
                <span class="model-load-error">${modelLoadError}</span>
              `,
              select: () => {},
            }),
          ],
        })
      );
    } else {
      groups.push(...modelGroups);
    }
    if (thinkingItems.length > 0) {
      groups.push(menu.group({ items: [...thinkingItems] }));
    }
    if (keySourceItems.length > 0) {
      groups.push(menu.group({ items: [...keySourceItems] }));
    }

    const boundary = getChatInputPortalReferenceElement(this, element);
    const menuWidth = `min(${PREFERENCE_MENU_WIDTH}, ${streamLainDropdownMenuBoundaryWidthCss(
      PREFERENCE_MENU_WIDTH
    )})`;
    this._currentMenuHandler = popMenu(popupTargetFromBodyElement(element), {
      options: {
        items: groups,
        testId: 'chat-input-preference',
      },
      placement: 'top-end',
      middleware: streamLainDropdownMenuMiddleware({
        horizontalBoundary: boundary,
      }),
    });
    this.menuOpen = true;
    const currentHandler = this._currentMenuHandler;
    configureStreamLainDropdownMenu(currentHandler, element, {
      layout: {
        width: menuWidth,
        minWidth: menuWidth,
        maxWidth: streamLainDropdownMenuBoundaryWidthCss('calc(100vw - 16px)'),
      },
      onClosed: () => {
        if (this._currentMenuHandler === currentHandler) {
          this._currentMenuHandler = null;
          this.menuOpen = false;
        }
      },
    });
  }

  override render() {
    const fullDisplayName = this._getFullDisplayName();
    const { brandPrefix, modelName } = modelTriggerLabelParts(fullDisplayName);
    return html`<button
      @click=${this.openPreference}
      data-testid="chat-input-preference-trigger"
      data-menu-open=${this.menuOpen}
      class="chat-input-preference-trigger"
      aria-haspopup="menu"
      aria-expanded=${this.menuOpen ? 'true' : 'false'}
    >
      <span
        class="chat-input-preference-trigger-label"
        title=${fullDisplayName}
      >
        ${brandPrefix && !this.hideBrandPrefix
          ? html`<span class="chat-input-preference-trigger-brand">
                ${brandPrefix}
              </span>
              <span class="chat-input-preference-trigger-model">
                ${modelName}
              </span>`
          : html`<span class="chat-input-preference-trigger-model">
              ${modelName}
            </span>`}
      </span>
    </button>`;
  }
}
